// Маскированный ввод даты «ДД.ММ.ГГГГ» вместо нативного <input type="date">.
//
// Зачем: нативный date-picker на iPhone (колесо / календарь без быстрого выбора
// года) крайне неудобен, особенно для даты рождения. Дату человек обычно знает
// наизусть — печатать быстрее любого пикера. Цифровая клавиатура, точки сами,
// валидация остаётся в zod-схемах потребителя (round-trip / диапазон).
//
// Контракт значения: наружу всегда ISO "YYYY-MM-DD" (или "" если не введено
// полностью). Внутри — отображаемый текст "ДД.ММ.ГГГГ". Позиционная конверсия
// БЕЗ валидации календаря — корректность («31.02») проверяет схема поля.

import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { FormField } from '@/components/forms/FormField';

const MASK_PLACEHOLDER = 'ДД.ММ.ГГГГ';

/** "1985-03-15" → "15.03.1985"; невалидный ISO → "". */
export function isoToMask(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '';
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/**
 * Голые цифры → "ДД.ММ.ГГГГ" БЕЗ хвостовой точки. Используется при удалении и
 * ресинке: "15" → "15", "1503" → "15.03". (Точка не «прилипает», её можно стереть.)
 */
export function digitsToMask(digits: string): string {
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  let out = dd;
  if (digits.length > 2) out += '.' + mm;
  if (digits.length > 4) out += '.' + yyyy;
  return out;
}

/**
 * Голые цифры → "ДД.ММ.ГГГГ" С хвостовой точкой после завершённого сегмента —
 * для печати вперёд: "14" → "14.", "1403" → "14.03.", "15031985" → "15.03.1985".
 * Точка появляется сразу как сигнал «дальше месяц/год».
 */
export function digitsToMaskTyping(digits: string): string {
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  let out = dd;
  if (digits.length >= 2) out += '.';
  out += mm;
  if (digits.length >= 4) out += '.';
  out += yyyy;
  return out;
}

/** 8 цифр → позиционный ISO "YYYY-MM-DD"; иначе "". Без проверки существования даты. */
export function digitsToIso(digits: string): string {
  if (digits.length !== 8) return '';
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  return `${yyyy}-${mm}-${dd}`;
}

/** Существует ли такая календарная дата (ловит 31.02, 30.04 и т.п.). */
export function isRealIsoDate(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const d = new Date(iso + 'T00:00:00Z');
  return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso;
}

const onlyDigits = (s: string): string => s.replace(/\D/g, '');

/**
 * Controlled-логика маски: держит отображаемый текст, ресинкается при внешней
 * смене ISO-значения (edit-load / reset), не затирая ввод пользователя.
 */
function useMaskedDate(value: string, onChange: (iso: string) => void) {
  const [text, setText] = useState<string>(() => isoToMask(value));
  // Длина предыдущего отображаемого текста — чтобы отличить ввод от удаления.
  const prevLen = useRef<number>(text.length);

  useEffect(() => {
    // Ресинк только если внешний ISO реально отличается от того, что сейчас в тексте
    // (иначе эффект затирал бы частичный/невалидный ввод во время печати).
    const currentIso = digitsToIso(onlyDigits(text));
    if (value !== currentIso) {
      const next = isoToMask(value);
      setText(next);
      prevLen.current = next.length;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleRaw = (raw: string) => {
    // Удаление (raw короче прежнего) → маска БЕЗ хвостовой точки, иначе авто-точка
    // тут же вернулась бы и её нельзя было бы стереть (backspace-ловушка).
    // Ввод вперёд → авто-точка после завершённого сегмента (14 → 14.).
    const deleting = raw.length < prevLen.current;
    const digits = onlyDigits(raw).slice(0, 8);
    const masked = deleting ? digitsToMask(digits) : digitsToMaskTyping(digits);
    prevLen.current = masked.length;
    setText(masked);
    onChange(digitsToIso(digits));
  };

  return { text, handleRaw };
}

type BareProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: string; // ISO "YYYY-MM-DD" или ""
  onChange: (iso: string) => void;
};

/** Голый <input> с маской — для контекстов со своей обёрткой label/error. */
export const MaskedDateInput = forwardRef<HTMLInputElement, BareProps>(function MaskedDateInput(
  { value, onChange, className, ...rest },
  ref,
) {
  const { text, handleRaw } = useMaskedDate(value, onChange);
  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={MASK_PLACEHOLDER}
      maxLength={10}
      value={text}
      onChange={(e) => handleRaw(e.target.value)}
      className={className}
      {...rest}
    />
  );
});

type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (iso: string) => void;
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
};

/** Полное поле (shadcn FormField — label + Input + error) для форм типа анкеты. */
export const DateField = forwardRef<HTMLInputElement, FieldProps>(function DateField(
  { value, onChange, label, error, hint, className, ...rest },
  ref,
) {
  const { text, handleRaw } = useMaskedDate(value, onChange);
  return (
    <FormField
      ref={ref}
      label={label}
      error={error}
      hint={hint}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={MASK_PLACEHOLDER}
      maxLength={10}
      value={text}
      onChange={(e) => handleRaw(e.target.value)}
      className={className}
      {...rest}
    />
  );
});
